import tippy from 'tippy.js'

// The browser's own `title` tooltip waits about a second and cannot be styled. Inside the v4 chart every
// `title` is turned into a quick tippy tooltip that uses the app palette (see `.tippy-box[data-theme~=cpm]`
// in resources/css/app.css). A MutationObserver covers Alpine-bound `:title` updates and Livewire re-renders.
const rootSelector = '[data-chart-v4-root]'
const processed = new WeakMap()

export function enhanceTitle(element, tippyFactory = tippy) {
    const text = element.getAttribute('title')

    if (text === null) return

    element.removeAttribute('title')

    if (!text.trim()) return

    // Keep the control named for screen readers now that the native title is gone.
    if (!element.hasAttribute('aria-label') && !element.textContent.trim()) {
        element.setAttribute('aria-label', text)
    }

    const existing = processed.get(element)

    if (existing) {
        existing.setContent(text)
        return
    }

    processed.set(element, tippyFactory(element, {
        content: text,
        theme: 'cpm',
        delay: [60, 0],
        duration: [90, 60],
        animation: 'scale',
        placement: 'bottom',
        arrow: false,
        offset: [0, 8],
        maxWidth: 280,
        appendTo: () => document.body,
    }))
}

export function enhanceAll(scope, tippyFactory = tippy) {
    scope.querySelectorAll('[title]').forEach(element => enhanceTitle(element, tippyFactory))
}

export function registerFastTooltips(browserWindow, documentObject) {
    const run = () => {
        documentObject.querySelectorAll(rootSelector).forEach(root => {
            enhanceAll(root)

            if (root.dataset.fastTooltips) return

            root.dataset.fastTooltips = '1'

            new browserWindow.MutationObserver(records => {
                for (const record of records) {
                    if (record.type === 'attributes') {
                        if (record.target.hasAttribute('title')) enhanceTitle(record.target)
                    } else {
                        record.addedNodes.forEach(node => {
                            if (node.nodeType !== 1) return
                            if (node.hasAttribute('title')) enhanceTitle(node)
                            enhanceAll(node)
                        })
                    }
                }
            }).observe(root, {subtree: true, childList: true, attributes: true, attributeFilter: ['title']})
        })
    }

    documentObject.addEventListener('alpine:initialized', run)
    documentObject.addEventListener('livewire:navigated', run)
    documentObject.addEventListener('DOMContentLoaded', run)
    run()
}
